import { FunctionComponent, useMemo } from "react";
import { CompactCardSelect, ScrollContainer } from "@animech/ui";
import { FieldProps } from "@animech/axiom-ui-builder";
import * as Styled from "./horizontal-card-select.style";
import { Root } from "@definitions";

export type ExtendedHorizontalCardSelectProps = {
	showLabel?: boolean;
	hiddenValues?: unknown[];
	padImages?: boolean;
};

export type HorizontalCardSelectProps = FieldProps<Root> &
	ExtendedHorizontalCardSelectProps;

export const HorizontalCardSelect: FunctionComponent<
	HorizontalCardSelectProps
> = ({
	fieldValues = [],
	value,
	onValueChange,
	fieldMeta,
	fieldValuesMeta,
	showLabel = false,
	hiddenValues,
	padImages,
}) => {
	const isHiddenValue =
		hiddenValues != null &&
		hiddenValues.length > 0 &&
		hiddenValues.includes(value);

	const { selectableValues, selectableMeta } = useMemo(() => {
		if (!hiddenValues || hiddenValues.length === 0) {
			return {
				selectableValues: fieldValues,
				selectableMeta: fieldValuesMeta,
			};
		}
		const indices: number[] = [];
		const values = fieldValues.filter((v, i) => {
			const keep = !hiddenValues.includes(v);
			if (keep) {
				indices.push(i);
			}
			return keep;
		});
		return {
			selectableValues: values,
			selectableMeta: fieldValuesMeta
				? indices.map((i) => fieldValuesMeta[i])
				: undefined,
		};
	}, [fieldValues, fieldValuesMeta, hiddenValues]);

	// When current value is OOS, prepend it to the card list so it shows as selected
	const { items, labels, images } = useMemo(() => {
		let vals = selectableValues;
		let meta = selectableMeta;
		if (isHiddenValue) {
			const oosIndex = fieldValues.indexOf(value);
			const oosMeta = fieldValuesMeta?.[oosIndex];
			vals = [value, ...selectableValues];
			meta = oosMeta ? [oosMeta, ...(selectableMeta ?? [])] : selectableMeta;
		}
		return {
			items: vals,
			labels: vals.map((v, i) => meta?.[i]?.label ?? String(v ?? "")),
			images: meta ? vals.map((_, i) => meta?.[i]?.image ?? "") : undefined,
		};
	}, [
		selectableValues,
		selectableMeta,
		isHiddenValue,
		value,
		fieldValues,
		fieldValuesMeta,
	]);

	if (items.length === 0) {
		return null;
	}

	return (
		<Styled.Root
			data-pad-images={padImages ? "" : undefined}
			data-oos={isHiddenValue ? "" : undefined}
		>
			{showLabel && fieldMeta?.label && (
				<Styled.FieldLabel>{fieldMeta.label}</Styled.FieldLabel>
			)}
			<ScrollContainer horizontal>
				<CompactCardSelect
					color="neutral"
					items={items}
					value={value}
					onValueChange={onValueChange}
					itemLabels={labels}
					itemImages={images}
				/>
			</ScrollContainer>
		</Styled.Root>
	);
};
